/* eslint-disable @typescript-eslint/no-empty-interface */
/* eslint-disable @typescript-eslint/naming-convention */
import PropTypes, { InferProps } from 'prop-types';
import { CssBaseline } from '@mui/material';
import { ThemeProvider as MUIThemeProvider, createTheme, StyledEngineProvider } from '@mui/material/styles';
//import PloniWoff2 from 'assets/fonts/ploni-regular-aaa.woff2';
import rtlPlugin from 'stylis-plugin-rtl';
import palette from './palette';
import { typography } from './typography';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import { Directions } from '@mui/icons-material';

const sizes = {
	DRAWER_WIDTH: 256,
	DRAWER_WIDTH_CLOSED: 62,
	APPBAR_MOBILE: 68,
	APP_BAR_DESKTOP: 92,
	CONTAINER_WIDTH: 1366,
	INPUT_HIGHT: 56,
	CARD_WIDTH: 630,
	STEPPER_WIDTH: 1000,
	CARD_TEXTAREA: 240,
};

declare module '@mui/material/styles' {
	interface ICustomTheme {
		sizes: {
			DRAWER_WIDTH: number;
			DRAWER_WIDTH_CLOSED: number;
			APPBAR_MOBILE: number;
			APP_BAR_DESKTOP: number;
			CONTAINER_WIDTH: number;
			INPUT_HIGHT: number;
			CARD_WIDTH: number;
			STEPPER_WIDTH: number;
			CARD_TEXTAREA: number;
		};
	}

	interface Theme extends ICustomTheme {}
	interface ThemeOptions extends ICustomTheme {}
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
	interface TypographyPropsVariantOverrides {
		boldBody1: true;
		boldBody2: true;
		boldBody3: true;
		boldBody4: true;
		boldBody5: true;
		boldBody6: true;
		boldBody7: true;
		subtitleBold: true;
		body3: true;
		body4: true;
		boldBody17: true;
		boldBody18: true;
		semiCaption: true;
		captionRegular: true;
		semiCaptionRegular: true;
		boldMedium: true;
		lightSmall: true;
		boldExtraLarge: true;
		boldSmall: true;
		boldStat: true;
		alertBody: true;
		body5: true;
		pageTitle: true;
		body: true;
		subtitle: true;
	}
}
const cacheRtl = createCache({
	key: 'muirtl',
	stylisPlugins: [prefixer, rtlPlugin],
});

export const ThemeProvider: any = ({ children, dir }: InferProps<typeof ThemeProvider.propTypes>) => {
	const theme = createTheme({
		palette,
		typography,
		shape: { borderRadius: 0 },
		sizes,
		components: {
			MuiTextField: {
				styleOverrides: {
					root: {
						'&.MuiFormControl-root': {
							width: '100%',
							'&:has(input[disabled])': {
								'& .MuiOutlinedInput-notchedOutline': {
									border: 0,
								},
								'& .MuiInputBase-root': {
									border: '1px solid #85A09E',
								},
							},
							'& .MuiInputBase-root': {
								borderRadius: '8px',
								'& .Mui-disabled': {
									backgroundColor: palette.root.basic6,
									borderRadius: '8px',
									color: '#5E5E5E',
									WebkitTextFillColor: '#5E5E5E',
								},
							},
						},
						'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
							border: '1px solid #3DB37B!important',
						},
					},
				},
			},
			MuiDialog: {
				styleOverrides: {
					root: {
						'& .MuiDialog-paper': {
							borderRadius: '8px',
							minWidth: sizes.CARD_WIDTH,
							padding: '24px',
							gap: '24px',
							boxBorder: '',
							'& .MuiDialogTitle-root, & .MuiDialogActions-root, & .MuiDialogContent-root': {
								padding: 0,
								direction: 'ltr',
								width: '100%',
							},
						},
					},
				},
			},
			MuiFormGroup: {
				styleOverrides: {
					root: {
						'& .fieldGroup-row .MuiBox-root': {
							width: '100%',
							'& .MuiInputBase-root': {
								width: '100%',
							},
						},
					},
				},
			},
			MuiButton: {
				styleOverrides: {
					root: {
						'&.buttonForm': {
							minWidth: '154px',
							borderRadius: '8px',
							height: '48px',
							maxWidth: 'fit-content',
						},
					},
				},
			},
		},
	});

	return dir === 'rtl' ? (
		<StyledEngineProvider injectFirst>
			<CacheProvider value={cacheRtl}>
				<MUIThemeProvider theme={theme}>
					<CssBaseline />
					{children}
				</MUIThemeProvider>
			</CacheProvider>
		</StyledEngineProvider>
	) : (
		<StyledEngineProvider injectFirst>
			<MUIThemeProvider theme={theme}>
				<CssBaseline />
				{children}
			</MUIThemeProvider>
		</StyledEngineProvider>
	);
};

ThemeProvider.propTypes = {
	children: PropTypes.node,
	dir: PropTypes.string,
};
